import React from 'react';
import { useCMS } from '../context/CMSContext';
import SEO from '../components/SEO';
import Gallery from '../components/Gallery';

export default function About() {
  const { getPage, getGallery } = useCMS();
  const page = getPage('om-equigramvet');
  const certGallery = getGallery('certificeringer');

  if (!page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-8 w-48 bg-brand-sand/40 rounded" />
          <div className="h-4 w-64 bg-brand-sand/30 rounded" />
        </div>
      </div>
    );
  }

  const { title, content, meta_title, meta_description } = page;

  // Hjælper til Cloudflare on-the-fly resizing af billeder
  const getResizedUrl = (url, width = 1200, quality = 80) => {
    return url.replace(
      'https://assets.auxodev.dk/assets/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/assets/`
    );
  };

  const aboutImage = 'https://assets.auxodev.dk/assets/19a97e0e-8e3c-4b11-bc4c-e5bcc52ff735/equine-portrait.jpg';

  const careerMilestones = [
    {
      period: 'Januar 2023',
      role: 'Uddannet dyrlæge',
      place: 'Københavns Universitet'
    },
    {
      period: 'Efterfølgende',
      role: 'Stordyrsdyrlæge i Nordsverige',
      place: 'Distriktsveterinärerna Vännäs/Umåker'
    },
    {
      period: '1 år',
      role: 'Rotations internship',
      place: 'Equitom (Europas største hestehospital i Belgien)'
    },
    {
      period: 'Klinikdyrlæge',
      role: 'Universitetshospitalet for Store Husdyr',
      place: 'Taastrup'
    },
    {
      period: 'Nuværende',
      role: 'Hestedyrlæge på Vestermose Dyreklinik',
      place: 'Med forkærlighed for sportsmedicin og rehabilitering'
    },
    {
      period: 'Igangværende uddannelse',
      role: 'Akupunktør for heste & kurser i sportsmedicin og rehabilitering',
      place: 'Løbende videreuddannelse'
    }
  ];

  return (
    <>
      <SEO 
        title={title?.da || 'Om EquiGramVet'} 
        description={meta_description?.da} 
      />

      {/* Header Sektion */}
      <section className="bg-brand-beige/50 border-b border-brand-sand/50 py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown mb-3 block">
            Hvem er vi
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-brand-dark">
            {title?.da}
          </h1>
          <p className="text-brand-brown text-sm md:text-base mt-4 max-w-xl mx-auto">
            Høj faglighed, autoriseret dyrlægeviden og en dyb kærlighed til dyr danner grundlaget for alt vores arbejde.
          </p>
          
          {/* Hurtig-navigation til sektioner */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <a 
              href="#om" 
              className="text-xs font-medium px-4 py-1.5 rounded-full bg-brand-cream border border-brand-sand/60 text-brand-brown hover:text-brand-dark hover:border-brand-brown/40 transition-colors"
            >
              Profil & Værdier
            </a>
            <a 
              href="#karriere" 
              className="text-xs font-medium px-4 py-1.5 rounded-full bg-brand-cream border border-brand-sand/60 text-brand-brown hover:text-brand-dark hover:border-brand-brown/40 transition-colors"
            >
              Karriere
            </a>
            <a 
              href="#certificeringer" 
              className="text-xs font-medium px-4 py-1.5 rounded-full bg-brand-cream border border-brand-sand/60 text-brand-brown hover:text-brand-dark hover:border-brand-brown/40 transition-colors"
            >
              Certificeringer
            </a>
          </div>
        </div>
      </section>

      {/* Indhold Sektion */}
      <section id="om" className="py-16 md:py-24 bg-brand-cream scroll-mt-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Præsentationsbillede */}
            <div className="lg:col-span-5 relative">
              <div className="aspect-[4/5] rounded-brand overflow-hidden border border-brand-sand/60 shadow-xl bg-brand-sand/20">
                <img
                  src={getResizedUrl(aboutImage, 800, 85)}
                  alt="EquiGramVet - Roligt staldmiljø og omsorg for dyret"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Dekorativ ramme-effekt */}
              <div className="absolute -inset-4 border border-brand-sand/30 rounded-brand -z-10 pointer-events-none translate-x-2 translate-y-2" />
            </div>

            {/* Tekstindhold */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div 
                className="prose prose-stone prose-headings:font-serif prose-headings:font-bold prose-headings:text-brand-dark prose-p:text-brand-brown prose-p:leading-relaxed prose-li:text-brand-brown prose-strong:text-brand-dark max-w-none"
                dangerouslySetInnerHTML={{ __html: content?.da || '' }}
              />

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6 border-t border-brand-sand/50 pt-8">
                <div className="p-5 rounded-brand bg-brand-beige/40 border border-brand-sand/50">
                  <h4 className="font-serif text-base font-bold text-brand-dark mb-2 flex items-center gap-2">
                    <i className="fa-regular fa-compass text-brand-brown" /> Vores Vision
                  </h4>
                  <p className="text-brand-brown text-xs leading-relaxed">
                    At fremme dyrs sundhed, bevægelighed og livskvalitet gennem skånsomme, naturlige og videnskabeligt funderede behandlingsmetoder.
                  </p>
                </div>
                <div className="p-5 rounded-brand bg-brand-beige/40 border border-brand-sand/50">
                  <h4 className="font-serif text-base font-bold text-brand-dark mb-2 flex items-center gap-2">
                    <i className="fa-regular fa-heart text-brand-brown" /> Vores Tilgang
                  </h4>
                  <p className="text-brand-brown text-xs leading-relaxed">
                    Vi behandler altid dyret på dets egne præmisser. Vi tvinger aldrig et dyr igennem en behandling, men opbygger tillid gennem ro og tålmodighed.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Karriere Sektion */}
      <section id="karriere" className="py-16 md:py-24 bg-brand-beige/25 border-t border-brand-sand/40 scroll-mt-28">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown mb-2 block">
              Baggrund & Erfaring
            </span>
            <h2 className="text-2xl md:text-4xl font-serif font-bold text-brand-dark">
              Karriere
            </h2>
          </div>

          {/* Kundens karrieretekst */}
          <div className="bg-brand-cream border border-brand-sand/60 rounded-brand p-8 sm:p-10 shadow-xs mb-10">
            <div className="prose prose-stone max-w-none text-brand-brown leading-relaxed space-y-4 text-base sm:text-lg">
              <p>
                Uddannet dyrlæge i januar 2023, efterfølgende arbejdet som stordyrsdyrlæge i Nordsverige (distriktsveterinärerna Vännäs/Umåker), efterfølgende taget 1 års rotations internship på Europas største hestehospital i Belgien, Equitom, og siden arbejdet på Universitetshospitalet for Store Husdyr i Taastrup som Klinikdyrlæge og nu som hestedyrlæge på Vestermose Dyreklinik med forkærlighed for sportsmedicin og rehabilitering.
              </p>
              <p>
                Og er på nuværende tidspunkt ved at uddanne mig som akupunktør for heste og tager yderligere kurser for at dygtiggøre mig indenfor sportsmedicin og rehabilitering.
              </p>
            </div>
          </div>

          {/* Tidslinje overblik */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {careerMilestones.map((item, idx) => (
              <div 
                key={idx} 
                className="p-5 rounded-brand bg-brand-cream/80 border border-brand-sand/50 flex flex-col justify-between"
              >
                <div>
                  <span className="text-[11px] font-semibold tracking-wider uppercase text-brand-brown/80 block mb-1">
                    {item.period}
                  </span>
                  <h3 className="font-serif font-bold text-sm sm:text-base text-brand-dark mb-1">
                    {item.role}
                  </h3>
                  <p className="text-xs text-brand-brown">
                    {item.place}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-brand-sand/30 flex items-center gap-1.5 text-[11px] text-brand-brown/70">
                  <i className="fa-regular fa-check text-brand-brown" />
                  <span>Faglig erfaring</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certificeringer Sektion */}
      <section id="certificeringer" className="py-16 md:py-24 bg-brand-cream border-t border-brand-sand/40 scroll-mt-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown mb-2 block">
              Uddannelse & Beviser
            </span>
            <h2 className="text-2xl md:text-4xl font-serif font-bold text-brand-dark">
              Certificeringer
            </h2>
            <p className="text-brand-brown text-sm sm:text-base mt-3 max-w-xl mx-auto">
              Dokumentation for veterinærfaglig efteruddannelse, eksamener og certificeringer som veterinærkiropraktor.
            </p>
          </div>

          {/* Certificerings-info bokse */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="p-6 rounded-brand bg-brand-beige/30 border border-brand-sand/60 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-brand-cream border border-brand-sand flex items-center justify-center text-brand-dark text-xl shrink-0">
                <i className="fa-regular fa-award" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg text-brand-dark mb-1">
                  Certificeret Veterinærkiropraktor
                </h3>
                <p className="text-xs sm:text-sm text-brand-brown leading-relaxed">
                  Gennemført og bestået post-graduate uddannelse i veterinær kiropraktik med speciale i både heste og hunde.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-brand bg-brand-beige/30 border border-brand-sand/60 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-brand-cream border border-brand-sand flex items-center justify-center text-brand-dark text-xl shrink-0">
                <i className="fa-regular fa-user-doctor" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg text-brand-dark mb-1">
                  Autoriseret Dyrlæge (DVM)
                </h3>
                <p className="text-xs sm:text-sm text-brand-brown leading-relaxed">
                  Autoriseret dyrlægeuddannelse fra Københavns Universitet med stærk baggrund inden for stordyr, hestehospital og sportsmedicin.
                </p>
              </div>
            </div>
          </div>

          {/* Galleri af Certificeringer & Diplomer */}
          <div className="mt-8">
            <Gallery gallerySlug="certificeringer" />
          </div>
        </div>
      </section>

      {/* Tillids-sektion */}
      <section className="py-16 md:py-20 bg-brand-beige/20 border-t border-brand-sand/30">
        <div className="mx-auto max-w-5xl px-6 text-center flex flex-col items-center gap-6">
          <div className="w-12 h-12 rounded-full bg-brand-dark text-brand-cream flex items-center justify-center text-xl">
            <i className="fa-light fa-shield-heart" />
          </div>
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-brand-dark">
            Din tryghed er vores højeste prioritet
          </h2>
          <p className="text-brand-brown text-sm md:text-base leading-relaxed max-w-3xl">
            Når du overlader dit dyr til EquiGramVet, kan du være sikker på, at vi behandler det med samme omsorg, grundighed og tålmodighed, som var det vores eget. Vi lytter til dine observationer som ejer, og vi samarbejder tæt om at skabe de bedste resultater for dit dyrs trivsel.
          </p>
        </div>
      </section>
    </>
  );
}
